import type { CustomerWorkOrder, OrderStatus } from "@/api/types";

// ─── Visibility ──────────────────────────────────────────────────────────────

/** DOM id of the Execution section: the `#execution` hash that notifications deep-link to. */
export const EXECUTION_SECTION_ID = "execution";

/**
 * Work orders exist only once the order is accepted and paid, and the order stays
 * `COMPLETED` afterwards, so these are the only statuses worth fetching them for.
 */
const EXECUTION_ORDER_STATUSES: readonly OrderStatus[] = ["ACCEPTED", "COMPLETED"];

export function shouldShowExecution(orderStatus: OrderStatus | string | undefined): boolean {
  return (
    orderStatus != null && (EXECUTION_ORDER_STATUSES as readonly string[]).includes(orderStatus)
  );
}

// ─── Timeline ────────────────────────────────────────────────────────────────

export type TimelineDotStatus = "default" | "active" | "completed" | "inactive" | "error";

/** Colour of a work order's dot on the Execution timeline. */
export function timelineDotStatus(workOrder: Pick<CustomerWorkOrder, "status">): TimelineDotStatus {
  switch (workOrder.status) {
    case "COMPLETED":
      return "completed";
    case "IN_PROGRESS":
      return "active";
    case "CANCELLED":
      return "error";
    default:
      return "default";
  }
}

/** The API already returns sequence order; sorting keeps the timeline right if that changes. */
export function sortWorkOrdersBySequence(workOrders: CustomerWorkOrder[]): CustomerWorkOrder[] {
  return [...workOrders].sort((a, b) => a.sequence - b.sequence);
}

/** The vendor has not picked a driver, truck or container yet. */
export function isAwaitingAssignment(
  workOrder: Pick<CustomerWorkOrder, "driver" | "truck" | "container">,
): boolean {
  return !workOrder.driver && !workOrder.truck && !workOrder.container;
}

// ─── Formatting ──────────────────────────────────────────────────────────────

/**
 * Display text for a `scheduledDate` (`YYYY-MM-DD`). The string is a Riyadh business day
 * with no time, so it is formatted from its parts: going through `new Date("YYYY-MM-DD")`
 * would parse as UTC midnight and show the previous day west of UTC.
 */
export function formatScheduledDate(date: string | null, locale: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? "");
  if (!match) return null;
  const [, year, month, day] = match;
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(Number(year), Number(month) - 1, Number(day)));
}

/** Localized delivery-window label, falling back to the raw `HH:mm–HH:mm` range. */
export function formatSlot(
  slot: CustomerWorkOrder["deliverySlot"],
  language: string,
): string | null {
  if (!slot) return null;
  const label = language.startsWith("ar") ? (slot.labelAr ?? slot.labelEn) : slot.labelEn;
  return label || `${slot.startTime}–${slot.endTime}`;
}

/** Local date and time of an ISO-8601 UTC timestamp (`startedAt`, `completedAt`, …). */
export function formatTimestamp(value: string | null, locale: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
}

// ─── Work order → order map (notification deep links) ───────────────────────

/**
 * `WORK_ORDER_ASSIGNED` / `WORK_ORDER_COMPLETED` notifications carry the work order id but
 * not its order id (open question Q2), and the customer API has no work-order lookup by id.
 * Until the backend adds `orderId` to the payload, the Execution section remembers
 * `workOrderId → orderId` for every work order it loads, so a later notification click can
 * open the right order.
 */
export const WORK_ORDER_ORDERS_STORAGE_KEY = "envirohub.customer.workOrderOrders";
/** Oldest entries are dropped beyond this many, so the map cannot grow without bound. */
export const WORK_ORDER_ORDERS_LIMIT = 200;

type WorkOrderOrderEntry = [workOrderId: string, orderId: string];

function isEntry(value: unknown): value is WorkOrderOrderEntry {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "string" &&
    typeof value[1] === "string"
  );
}

/** Storage can be missing, blocked, full or hold junk (private mode, cleared data): never throw. */
function readEntries(): WorkOrderOrderEntry[] {
  try {
    const raw = window.localStorage.getItem(WORK_ORDER_ORDERS_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isEntry) : [];
  } catch {
    return [];
  }
}

export function rememberWorkOrderOrders(
  orderId: string,
  workOrders: ReadonlyArray<Pick<CustomerWorkOrder, "id">>,
): void {
  if (!orderId || workOrders.length === 0) return;
  try {
    const known = readEntries();
    const knownOrderByWorkOrder = new Map(known);
    // The map is read on every 10s poll; skip the write when nothing changed.
    if (workOrders.every((wo) => knownOrderByWorkOrder.get(wo.id) === orderId)) return;

    const incoming = new Set(workOrders.map((wo) => wo.id));
    const next: WorkOrderOrderEntry[] = [
      ...known.filter(([workOrderId]) => !incoming.has(workOrderId)),
      ...workOrders.map((wo): WorkOrderOrderEntry => [wo.id, orderId]),
    ].slice(-WORK_ORDER_ORDERS_LIMIT);
    window.localStorage.setItem(WORK_ORDER_ORDERS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Remembering is best effort: without it the deep link falls back to the orders list.
  }
}

/** The order a work order belongs to, if the customer has opened it before. */
export function lookupOrderIdForWorkOrder(workOrderId: string): string | null {
  if (!workOrderId) return null;
  return readEntries().find(([id]) => id === workOrderId)?.[1] ?? null;
}
